package com.wintermar.inspection.core.ui.theme.color

import com.wintermar.inspection.core.ui.theme.color.model.AccentGroupThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.AccentToneThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.BgThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.BorderBrandThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.BorderInputThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.BorderThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.IconSemanticThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.IconThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SemanticStateThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SemanticToneThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceAccentThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceBrandPrimaryThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceBrandThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceGrayThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceInputThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceNavThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceNeutralThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceSemanticThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceStateThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.SurfaceThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.TextLinkThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.TextSemanticThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.TextThemeColor
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor

val DefaultThemeColor: ThemeColor = ThemeColor(
    bg = BgThemeColor(
        inverse = ColorPalette.Neutral.n800,
        normal = ColorPalette.Neutral.n25
    ),

    border = BorderThemeColor(
        bold = ColorPalette.Neutral.n400,
        default = ColorPalette.Neutral.n300,
        error = ColorPalette.Error.n300,
        info = ColorPalette.Info.n300,
        subtle = ColorPalette.Neutral.n200,
        success = ColorPalette.Success.n300,
        warning = ColorPalette.Warning.n300,
        brand = BorderBrandThemeColor(
            primary = ColorPalette.Brand.Primary.n600,
            secondary = ColorPalette.Brand.Secondary.n600
        ),
        input = BorderInputThemeColor(
            default = ColorPalette.Neutral.n200,
            disabled = ColorPalette.Neutral.n300,
            error = ColorPalette.Error.n600,
            focused = ColorPalette.Brand.Primary.n500,
            hover = ColorPalette.Neutral.n300,
            success = ColorPalette.Success.n600
        )
    ),

    icon = IconThemeColor(
        brand = ColorPalette.Brand.Primary.n600,
        default = ColorPalette.Neutral.n700,
        disabled = ColorPalette.Neutral.n300,
        inverse = ColorPalette.Neutral.n25,
        onBrand = ColorPalette.Basic.white,
        subtle = ColorPalette.Neutral.n500,
        subtlest = ColorPalette.Neutral.n400,
        error = IconSemanticThemeColor(
            bolder = ColorPalette.Error.n700,
            default = ColorPalette.Error.n600,
            onColor = ColorPalette.Error.n25
        ),
        info = IconSemanticThemeColor(
            bolder = ColorPalette.Info.n700,
            default = ColorPalette.Info.n600,
            onColor = ColorPalette.Info.n25
        ),
        success = IconSemanticThemeColor(
            bolder = ColorPalette.Success.n800,
            default = ColorPalette.Success.n700,
            onColor = ColorPalette.Success.n25
        ),
        warning = IconSemanticThemeColor(
            bolder = ColorPalette.Warning.n800,
            default = ColorPalette.Warning.n700,
            onColor = ColorPalette.Warning.n25
        )
    ),

    surface = SurfaceThemeColor(
        lowered = ColorPalette.Neutral.n100,
        normal = ColorPalette.Basic.white,
        raised = ColorPalette.Neutral.n25,
        accent = SurfaceAccentThemeColor(
            blue = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Blue.n800,
                    default = ColorPalette.Accent.Blue.n800,
                    hover = ColorPalette.Accent.Blue.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Blue.n700,
                    default = ColorPalette.Accent.Blue.n500,
                    hover = ColorPalette.Accent.Blue.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Blue.n300,
                    default = ColorPalette.Accent.Blue.n100,
                    hover = ColorPalette.Accent.Blue.n200
                )
            ),
            lime = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Lime.n800,
                    default = ColorPalette.Accent.Lime.n800,
                    hover = ColorPalette.Accent.Lime.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Lime.n700,
                    default = ColorPalette.Accent.Lime.n500,
                    hover = ColorPalette.Accent.Lime.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Lime.n300,
                    default = ColorPalette.Accent.Lime.n100,
                    hover = ColorPalette.Accent.Lime.n200
                )
            ),
            magenta = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Magenta.n800,
                    default = ColorPalette.Accent.Magenta.n800,
                    hover = ColorPalette.Accent.Magenta.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Magenta.n700,
                    default = ColorPalette.Accent.Magenta.n500,
                    hover = ColorPalette.Accent.Magenta.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Magenta.n300,
                    default = ColorPalette.Accent.Magenta.n100,
                    hover = ColorPalette.Accent.Magenta.n200
                )
            ),
            orange = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Orange.n800,
                    default = ColorPalette.Accent.Orange.n800,
                    hover = ColorPalette.Accent.Orange.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Orange.n700,
                    default = ColorPalette.Accent.Orange.n500,
                    hover = ColorPalette.Accent.Orange.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Orange.n300,
                    default = ColorPalette.Accent.Orange.n200,
                    hover = ColorPalette.Accent.Orange.n200
                )
            ),
            purple = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Purple.n800,
                    default = ColorPalette.Accent.Purple.n800,
                    hover = ColorPalette.Accent.Purple.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Purple.n700,
                    default = ColorPalette.Accent.Purple.n500,
                    hover = ColorPalette.Accent.Purple.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Purple.n300,
                    default = ColorPalette.Accent.Purple.n100,
                    hover = ColorPalette.Accent.Purple.n200
                )
            ),
            red = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Red.n800,
                    default = ColorPalette.Accent.Red.n800,
                    hover = ColorPalette.Accent.Red.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Red.n700,
                    default = ColorPalette.Accent.Red.n500,
                    hover = ColorPalette.Accent.Red.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Red.n300,
                    default = ColorPalette.Accent.Red.n100,
                    hover = ColorPalette.Accent.Red.n200
                )
            ),
            teal = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Teal.n800,
                    default = ColorPalette.Accent.Teal.n800,
                    hover = ColorPalette.Accent.Teal.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Teal.n700,
                    default = ColorPalette.Accent.Teal.n500,
                    hover = ColorPalette.Accent.Teal.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Teal.n300,
                    default = ColorPalette.Accent.Teal.n100,
                    hover = ColorPalette.Accent.Teal.n200
                )
            ),
            yellow = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Yellow.n800,
                    default = ColorPalette.Accent.Yellow.n800,
                    hover = ColorPalette.Accent.Yellow.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Yellow.n700,
                    default = ColorPalette.Accent.Yellow.n500,
                    hover = ColorPalette.Accent.Yellow.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Yellow.n300,
                    default = ColorPalette.Accent.Yellow.n100,
                    hover = ColorPalette.Accent.Yellow.n200
                )
            ),
            green = AccentGroupThemeColor(
                bold = AccentToneThemeColor(
                    active = ColorPalette.Accent.Green.n800,
                    default = ColorPalette.Accent.Green.n800,
                    hover = ColorPalette.Accent.Green.n900
                ),
                medium = AccentToneThemeColor(
                    active = ColorPalette.Accent.Green.n700,
                    default = ColorPalette.Accent.Green.n500,
                    hover = ColorPalette.Accent.Green.n600
                ),
                subtle = AccentToneThemeColor(
                    active = ColorPalette.Accent.Green.n300,
                    default = ColorPalette.Accent.Green.n100,
                    hover = ColorPalette.Accent.Green.n200
                )
            )
        ),
        brand = SurfaceBrandThemeColor(
            primary = SurfaceBrandPrimaryThemeColor(
                active = ColorPalette.Brand.Primary.n800,
                default = ColorPalette.Brand.Primary.n700,
                disabled = ColorPalette.Neutral.n100,
                hover = ColorPalette.Brand.Primary.n800,
                pressed = ColorPalette.Brand.Primary.n800
            ),
            primarySubtle = SurfaceBrandPrimaryThemeColor(
                active = ColorPalette.Brand.Primary.n50,
                default = ColorPalette.Brand.Primary.n25,
                disabled = ColorPalette.Neutral.n100,
                hover = ColorPalette.Brand.Primary.n50,
                pressed = ColorPalette.Brand.Primary.n50
            ),
            secondary = SurfaceBrandPrimaryThemeColor(
                active = ColorPalette.Brand.Secondary.n800,
                default = ColorPalette.Brand.Secondary.n700,
                disabled = ColorPalette.Neutral.n400,
                hover = ColorPalette.Brand.Secondary.n800,
                pressed = ColorPalette.Brand.Secondary.n800
            )
        ),
        gray = SurfaceGrayThemeColor(
            bold = SurfaceStateThemeColor(
                default = ColorPalette.Neutral.n700,
                hover = ColorPalette.Neutral.n800,
                pressed = ColorPalette.Neutral.n900
            ),
            subtle = SurfaceStateThemeColor(
                default = ColorPalette.Neutral.n100,
                hover = ColorPalette.Neutral.n200,
                pressed = ColorPalette.Neutral.n300
            )
        ),
        input = SurfaceInputThemeColor(
            active = ColorPalette.Basic.white,
            default = ColorPalette.Basic.white,
            disabled = ColorPalette.Neutral.n100,
            hover = ColorPalette.Neutral.n25
        ),
        nav = SurfaceNavThemeColor(
            bg = ColorPalette.Basic.white,
            bgHover = ColorPalette.Brand.Primary.n800,
            bgSelected = ColorPalette.Brand.Primary.n800
        ),
        neutral = SurfaceNeutralThemeColor(
            bold = SurfaceStateThemeColor(
                default = ColorPalette.Neutral.n700,
                hover = ColorPalette.Neutral.n800,
                pressed = ColorPalette.Neutral.n900
            ),
            bolder = SurfaceStateThemeColor(
                default = ColorPalette.Neutral.n900,
                hover = ColorPalette.Neutral.n800,
                pressed = ColorPalette.Neutral.n700
            ),
            subtle = SurfaceStateThemeColor(
                default = ColorPalette.Neutral.n100,
                hover = ColorPalette.Neutral.n200,
                pressed = ColorPalette.Neutral.n300
            ),
            subtlest = SurfaceStateThemeColor(
                default = ColorPalette.Neutral.n50,
                hover = ColorPalette.Neutral.n100,
                pressed = ColorPalette.Neutral.n200
            )
        ),
        semantic = SurfaceSemanticThemeColor(
            error = SemanticToneThemeColor(
                bold = SemanticStateThemeColor(
                    active = ColorPalette.Error.n500,
                    default = ColorPalette.Error.n600,
                    hover = ColorPalette.Error.n700,
                    pressed = ColorPalette.Error.n500
                ),
                subtle = SemanticStateThemeColor(
                    active = ColorPalette.Error.n400,
                    default = ColorPalette.Error.n50,
                    hover = ColorPalette.Error.n100,
                    pressed = ColorPalette.Error.n200
                )
            ),
            info = SemanticToneThemeColor(
                bold = SemanticStateThemeColor(
                    active = ColorPalette.Info.n700,
                    default = ColorPalette.Info.n700,
                    hover = ColorPalette.Info.n800,
                    pressed = ColorPalette.Info.n700
                ),
                subtle = SemanticStateThemeColor(
                    active = ColorPalette.Info.n300,
                    default = ColorPalette.Info.n50,
                    hover = ColorPalette.Info.n100,
                    pressed = ColorPalette.Info.n100
                )
            ),
            success = SemanticToneThemeColor(
                bold = SemanticStateThemeColor(
                    active = ColorPalette.Success.n700,
                    default = ColorPalette.Success.n700,
                    hover = ColorPalette.Success.n800,
                    pressed = ColorPalette.Success.n700
                ),
                subtle = SemanticStateThemeColor(
                    active = ColorPalette.Success.n300,
                    default = ColorPalette.Success.n50,
                    hover = ColorPalette.Success.n100,
                    pressed = ColorPalette.Success.n100
                )
            ),
            warning = SemanticToneThemeColor(
                bold = SemanticStateThemeColor(
                    active = ColorPalette.Warning.n700,
                    default = ColorPalette.Warning.n700,
                    hover = ColorPalette.Warning.n800,
                    pressed = ColorPalette.Warning.n700
                ),
                subtle = SemanticStateThemeColor(
                    active = ColorPalette.Warning.n300,
                    default = ColorPalette.Warning.n50,
                    hover = ColorPalette.Warning.n100,
                    pressed = ColorPalette.Warning.n100
                )
            )
        )
    ),

    text = TextThemeColor(
        brand = ColorPalette.Brand.Primary.n600,
        default = ColorPalette.Neutral.n700,
        disabled = ColorPalette.Neutral.n400,
        inverse = ColorPalette.Neutral.n25,
        onBrand = ColorPalette.Basic.white,
        subtle = ColorPalette.Neutral.n500,
        subtlest = ColorPalette.Neutral.n400,
        error = TextSemanticThemeColor(
            bolder = ColorPalette.Error.n800,
            default = ColorPalette.Error.n600,
            on = ColorPalette.Error.n25
        ),
        info = TextSemanticThemeColor(
            bolder = ColorPalette.Info.n700,
            default = ColorPalette.Info.n600,
            on = ColorPalette.Info.n25
        ),
        link = TextLinkThemeColor(
            error = ColorPalette.Error.n700,
            info = ColorPalette.Info.n700,
            primary = ColorPalette.Brand.Primary.n600,
            success = ColorPalette.Success.n700,
            visited = ColorPalette.Accent.Magenta.n900,
            warning = ColorPalette.Warning.n700
        ),
        success = TextSemanticThemeColor(
            bolder = ColorPalette.Success.n700,
            default = ColorPalette.Success.n700,
            on = ColorPalette.Success.n25
        ),
        warning = TextSemanticThemeColor(
            bolder = ColorPalette.Warning.n800,
            default = ColorPalette.Warning.n700,
            on = ColorPalette.Warning.n25
        )
    )
)
